import { useCallback, useEffect, useState } from "react";
import { ApiError, getNoteVersions, restoreNote } from "../api";
import type { NoteKind, NoteVersion } from "../api";
import { Time } from "../Time";
import { ROLE_LABEL } from "../pages/CustomerLayout";
import { lineDiff } from "./diff";
import { NOTE_META } from "./NoteCard";

/**
 * Every version of one note, newest first, each as the lines it changed.
 *
 * Fetched when opened rather than with the overview: most visits never
 * look at the history. Diffs are computed here, against the version just
 * older, and every line is rendered as text (invariant 21).
 */
export function HistoryDrawer({
  customer,
  kind,
  canEdit,
  onClose,
  onRestored,
}: {
  customer: string;
  kind: NoteKind;
  canEdit: boolean;
  onClose: () => void;
  /** Refetch the overview after a restore made a new current version. */
  onRestored: () => void;
}) {
  const [versions, setVersions] = useState<NoteVersion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [full, setFull] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setError(null);
    void getNoteVersions(customer, kind)
      .then(setVersions)
      .catch((e) => setError(e.message));
  }, [customer, kind]);

  useEffect(load, [load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const toggle = (v: number) =>
    setFull((s) => {
      const next = new Set(s);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });

  const restore = async (from: number) => {
    if (!versions?.length) return;
    setBusy(true);
    setError(null);
    try {
      await restoreNote(customer, kind, from, versions[0].version);
      onRestored();
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 409
          ? "Somebody saved a newer version just now. The list has been refreshed; check it before restoring."
          : e instanceof Error
            ? e.message
            : String(e),
      );
    } finally {
      setBusy(false);
      load();
    }
  };

  const latest = versions?.[0];

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-label={`${NOTE_META[kind].title} history`}>
        <div className="drawer-head">
          <div className="drawer-title">
            <strong>{NOTE_META[kind].title} — history</strong>
            {versions && (
              <div className="dim">
                {versions.length} {versions.length === 1 ? "version" : "versions"} · newest first
              </div>
            )}
          </div>
          <button className="tiny" onClick={onClose}>
            Close
          </button>
        </div>

        {error && <div className="error drawer-error">{error}</div>}

        {!versions ? (
          <div className="empty">Loading…</div>
        ) : (
          <ol className="versions">
            {versions.map((v, i) => {
              const older = versions[i + 1];
              const lines = lineDiff(older?.body ?? "", v.body);
              const isCurrent = i === 0;
              const canRestore = canEdit && !isCurrent && v.body !== latest?.body;
              return (
                <li key={v.version}>
                  <div className="version-head">
                    <span className="mono dim">v{v.version}</span>
                    <strong>{v.author_name ?? v.author_email}</strong>
                    <span className="chip tiny">{ROLE_LABEL[v.author_role]}</span>
                    {isCurrent && <span className="badge pos">Current</span>}
                    <span className="spacer" />
                    <span className="dim">
                      <Time value={v.created_at} />
                    </span>
                  </div>
                  {v.restored_from !== null && (
                    <div className="dim">Restored from v{v.restored_from}</div>
                  )}
                  <div className="diff">
                    {lines.length === 0 ? (
                      <div className="dim">No text changes</div>
                    ) : (
                      lines.map((l, k) => (
                        <div key={k} className={l.op}>
                          <span className="mono sign">{l.op === "add" ? "+" : "−"}</span>
                          <span className="line">{l.text}</span>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="version-actions">
                    <button className="tiny" onClick={() => toggle(v.version)}>
                      {full.has(v.version) ? "Hide full text" : "Show full text"}
                    </button>
                    {canRestore && (
                      <button className="tiny" onClick={() => restore(v.version)} disabled={busy}>
                        Restore this version
                      </button>
                    )}
                  </div>
                  {full.has(v.version) && <div className="version-full">{v.body}</div>}
                </li>
              );
            })}
          </ol>
        )}
      </aside>
    </>
  );
}
